/**
 * One resolver for "what do we call this person", used everywhere a name is shown.
 *
 * THE BUG THIS FIXES
 *
 * Taylor: "Top right profile icon says 'Set Your name' use the First Name and
 * Last Name from the profile or the Display Name (Optional). If that is how its
 * set its definitely not working."
 *
 * It wasn't. There are two independent sources of a user's name and the display
 * layer read the wrong one:
 *
 *   - `GET /api/me/profile` (ApiUserProfile) holds firstName, lastName and
 *     displayName. This is what the Settings profile form writes, and what the
 *     user thinks of as "my name".
 *   - Supabase `session.user.user_metadata.name` is a separate blob, written at
 *     signup and never touched by that form.
 *
 * AuthContext set `name: userMeta.name ?? ''`, TopBar passed that straight to
 * UserMenu, and UserMenu rendered `name || t('userMenu.noName')`. So a user with
 * a fully-filled profile saw "Set your name" — the profile was ignored entirely.
 *
 * The same file also had a second, different fallback (`user?.name || user?.email`)
 * a few hundred lines away, which is how two views of one person disagree.
 *
 * PRECEDENCE, and why
 *   1. profile.displayName  — an explicit choice about how to be addressed wins.
 *   2. first + last name    — the normal case.
 *   3. either name alone    — a half-filled profile is still better than nothing.
 *   4. user_metadata.name   — the legacy source, for accounts created before the
 *                             profile row existed.
 *   5. email local part     — a last resort that is still recognisably *them*.
 *   6. null                 — only when we genuinely know nothing. Callers show
 *                             their own prompt; this function never invents one,
 *                             so "Set your name" can only appear when it is true.
 */

export interface DisplayNameSources {
  /** From GET /api/me/profile — the canonical profile row. */
  profile?: {
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
  } | null;
  /** Legacy Supabase user_metadata.name. */
  metadataName?: string | null;
  /** Auth email, used for the local-part fallback. */
  email?: string | null;
}

const clean = (v: string | null | undefined): string => (typeof v === 'string' ? v.trim() : '');

/**
 * Best available name, or null when nothing is known.
 *
 * Never returns a prompt or a placeholder — a caller that wants "Set your name"
 * must supply it, so that string cannot appear over a real name.
 */
export function resolveUserDisplayName(sources: DisplayNameSources): string | null {
  const { profile, metadataName, email } = sources;

  const display = clean(profile?.displayName);
  if (display) return display;

  const first = clean(profile?.firstName);
  const last = clean(profile?.lastName);
  if (first && last) return `${first} ${last}`;
  if (first) return first;
  if (last) return last;

  const meta = clean(metadataName);
  if (meta) return meta;

  const local = clean(email).split('@')[0] ?? '';
  // Guard against a malformed address ('@example.com') yielding an empty string.
  if (local) return local;

  return null;
}

/**
 * Initials for an avatar, from the same precedence.
 *
 * Two letters where a full name is known, one otherwise. Returns '' rather than
 * a '?' so callers can choose their own empty state.
 */
export function resolveUserInitials(sources: DisplayNameSources): string {
  const name = resolveUserDisplayName(sources);
  if (!name) return '';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
  }
  return (parts[0]?.[0] ?? '').toUpperCase();
}
